<template>
  <div class="edit-photos-page">
    <v-container class="py-8">
      <!-- Header -->
      <div class="page-header mb-3">
        <div class="brand-header">
          <router-link to="/">
            <PassingOnLogo class="brand-logo-svg" :name2-override="memorial?.nameOfPerson ? '-' + memorial.nameOfPerson : null" />
          </router-link>
        </div>

        <!-- Profile Menu (Top Right) -->
        <ProfileMenu />
      </div>

      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-card class="main-card" elevation="0">
            <v-card-title class="text-h6 section-header primary--text">
              Edit Photos &amp; Voice Memories
            </v-card-title>

            <v-card-text class="pa-6">
              <!-- Instructions -->
              <div class="mb-6">
                <p class="text-h6 mb-4">
                  Add, edit, or remove photos of {{ memorial?.nameOfPerson || 'your loved one' }} that you would like friends and family to see
                </p>
              </div>

              <!-- Drag and Drop Area -->
              <div
                class="photo-drop-zone"
                :class="{ 'drag-over': isDragOver }"
                @dragover.prevent="handleDragOver"
                @dragleave.prevent="handleDragLeave"
                @drop.prevent="handleDrop"
                @click="triggerFileInput"
              >
                <input
                  ref="fileInput"
                  type="file"
                  multiple
                  accept="image/*,.heic,.HEIC"
                  style="display: none"
                  @change="handleFileSelect"
                />

                <v-icon size="64" color="primary" class="mb-4">
                  mdi-cloud-upload-outline
                </v-icon>
                <h4 class="text-h6 mb-2">Drop photos here or click to select</h4>
                <p class="text-body-2 text--secondary">
                  Supports JPG, PNG, GIF, HEIC files
                </p>
              </div>

              <!-- Photo List -->
              <div v-if="photos.length > 0 || uploadingPhotos.length > 0" class="photo-list mt-6">
                <!-- Existing/Uploaded Photos -->
                <div
                  v-for="(photo, index) in photos"
                  :key="`photo-${photo.uuid || index}`"
                  class="photo-row mb-4"
                >
                  <v-row>
                    <v-col cols="12" sm="4" md="3">
                      <div class="photo-container">
                        <v-img
                          :src="getPhotoUrl(photo)"
                          :alt="photo.title || 'Photo'"
                          aspect-ratio="1"
                          cover
                          class="photo-thumbnail"
                        >
                          <template v-slot:placeholder>
                            <v-row class="fill-height ma-0" align="center" justify="center">
                              <v-progress-circular indeterminate color="primary"></v-progress-circular>
                            </v-row>
                          </template>
                        </v-img>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="8" md="9">
                      <v-textarea
                        v-model="photo.title"
                        label="Photo Caption"
                        variant="outlined"
                        rows="4"
                        auto-grow
                        placeholder="Add a caption for this photo..."
                        class="mt-0 mb-2"
                      ></v-textarea>

                      <v-btn
                        color="error"
                        variant="text"
                        size="small"
                        @click="removePhoto(index)"
                      >
                        <v-icon left>mdi-delete</v-icon>
                        Remove Photo
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>

                <!-- Uploading Photos Placeholders -->
                <div
                  v-for="(uploadingPhoto, index) in uploadingPhotos"
                  :key="`uploading-${index}`"
                  class="photo-row mb-4"
                >
                  <v-row>
                    <v-col cols="12" sm="4" md="3">
                      <div class="photo-container uploading">
                        <v-img
                          :src="uploadingPhoto.preview"
                          aspect-ratio="1"
                          cover
                          class="photo-thumbnail"
                        ></v-img>
                        <div class="upload-overlay">
                          <v-progress-circular
                            indeterminate
                            color="white"
                            size="48"
                          ></v-progress-circular>
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="8" md="9">
                      <v-skeleton-loader type="article"></v-skeleton-loader>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <div v-else class="text-center py-8 text--secondary">
                <v-icon size="64" color="grey-lighten-1" class="mb-3">mdi-image-off</v-icon>
                <p>No photos yet. Upload some photos to get started.</p>
              </div>

              <v-divider class="my-8" />

              <section>
                <h3 class="text-h5 mb-2">Voice memories</h3>
                <p class="text-body-1 mb-4">Record a new voice memory or manage existing audio.</p>

                <div v-if="audios.length" class="audio-list mb-5">
                  <div v-for="(audio, index) in audios" :key="audio.uuid" class="mb-5">
                    <AudioEntry :media="audio" :memorial-slug="memorial?.shareSlug" />
                    <v-textarea
                      v-model="audio.title"
                      label="Note about this voice memory"
                      variant="outlined"
                      rows="2"
                      auto-grow
                      class="mt-3"
                    />
                    <v-btn color="error" variant="text" prepend-icon="mdi-delete" @click="removeAudio(index)">
                      Remove voice memory
                    </v-btn>
                  </div>
                </div>

                <VoiceContributionInput v-model="newAudios" />
              </section>
            </v-card-text>

            <v-card-actions class="pa-6 pt-0">
              <v-btn
                variant="text"
                @click="cancel"
                class="mr-4"
              >
                Back to Dashboard
              </v-btn>

              <v-spacer></v-spacer>

              <v-btn
                color="primary"
                size="large"
                rounded="0"
                elevation="0"
                :disabled="saving"
                :loading="saving"
                @click="savePhotos"
                class="px-8"
              >
                Save
                <v-icon right class="ml-2">mdi-content-save</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Error Dialog -->
    <v-dialog v-model="errorDialog.show" max-width="500">
      <v-card>
        <v-card-title class="bg-error text-white d-flex align-center">
          <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
          Error
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-body-1">{{ errorDialog.message }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="elevated"
            @click="errorDialog.show = false"
          >
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import apiService from '../services/api'
import { isSupportedImage, normalizeImageFile } from '../services/photoUpload'
import ProfileMenu from '../components/ProfileMenu.vue'
import AudioEntry from '../components/media/AudioEntry.vue'
import VoiceContributionInput from '../components/media/VoiceContributionInput.vue'

const router = useRouter()
const store = useMemorialStore()

// Refs
const fileInput = ref(null)
const memorial = ref(null)

// State
const photos = ref([])
const uploadingPhotos = ref([])
const audios = ref([])
const newAudios = ref([])
const isDragOver = ref(false)
const saving = ref(false)

// Photos to delete (track UUIDs)
const photosToDelete = ref([])
const audiosToDelete = ref([])

// Error dialog
const errorDialog = ref({
  show: false,
  message: ''
})

const showError = (message) => {
  errorDialog.value.message = message
  errorDialog.value.show = true
}

// Load memorial data
const loadMemorialData = async () => {
  try {
    const response = await apiService.getMemorial(store.memorialUuid)
    if (response.success && response.memorial) {
      memorial.value = response.memorial

      // Load existing photos
      if (response.memorial.media && response.memorial.media.length > 0) {
        photos.value = response.memorial.media.filter(media => media.mediaType === 0 || media.mediaType == null).map(media => ({
          uuid: media.uuid,
          filename: media.filename,
          extension: media.extension,
          location: media.location,
          title: media.title || '',
          mediaType: media.mediaType,
          isExisting: true
        }))
        audios.value = response.memorial.media.filter(media => media.mediaType === 4).map(media => ({
          ...media,
          title: media.title || ''
        }))
      }
    }
  } catch (err) {
    console.error('Error loading memorial:', err)
  }
}

const getPhotoUrl = (photo) => {
  if (photo.preview) {
    return photo.preview
  }
  if (photo.location) {
    return `${photo.location}/${photo.filename}.${photo.extension}`
  }
  return `https://assets.forever-x.com/photos/${photo.filename}.${photo.extension}`
}

// File handling
const handleDragOver = () => {
  isDragOver.value = true
}

const handleDragLeave = () => {
  isDragOver.value = false
}

const handleDrop = (e) => {
  isDragOver.value = false
  const files = Array.from(e.dataTransfer.files)
  processFiles(files)
}

const triggerFileInput = () => {
  fileInput.value.click()
}

const handleFileSelect = (e) => {
  const files = Array.from(e.target.files)
  processFiles(files)
  e.target.value = '' // Reset input
}

const processFiles = async (files) => {
  for (const file of files) {
    if (!isSupportedImage(file)) continue

    let processedFile
    let preview = ''

    try {
      processedFile = await normalizeImageFile(file)
      preview = URL.createObjectURL(processedFile)

      // Add to uploading list
      const uploadingPhoto = {
        file: processedFile,
        preview,
        name: processedFile.name
      }
      uploadingPhotos.value.push(uploadingPhoto)

      // Upload immediately
      const uploadedPhoto = await uploadPhoto(processedFile)
      if (uploadedPhoto) {
        // Add to photos list
        photos.value.push({
          ...uploadedPhoto,
          title: uploadedPhoto.title || '',
          isExisting: false
        })
      }
    } catch (err) {
      console.error('Error uploading photo:', err)
      showError('Failed to upload photo: ' + (file?.name || 'file'))
    } finally {
      // Remove from uploading list
      const index = uploadingPhotos.value.findIndex(p => p.preview === preview)
      if (index !== -1) {
        URL.revokeObjectURL(preview)
        uploadingPhotos.value.splice(index, 1)
      }
    }
  }
}

const uploadPhoto = async (file) => {
  const response = await apiService.uploadPhoto(
    store.memorialUuid,
    file,
    '',
    store.apiToken
  )

  if (response.success && response.media) {
    return response.media
  }

  throw new Error(response.msg || 'Upload failed')
}

const removePhoto = (index) => {
  const photo = photos.value[index]

  // If it's an existing photo, track it for deletion
  if (photo.isExisting && photo.uuid) {
    photosToDelete.value.push(photo.uuid)
  }

  // Remove from photos array
  photos.value.splice(index, 1)
}

const removeAudio = index => {
  const audio = audios.value[index]
  if (audio?.uuid) audiosToDelete.value.push(audio.uuid)
  audios.value.splice(index, 1)
}

const savePhotos = async () => {
  saving.value = true

  try {
    // Prepare photos data (only include uuid and title)
    const photosData = photos.value.map(photo => ({
      uuid: photo.uuid,
      title: photo.title || ''
    }))

    // Make a single batch API call
    const response = await apiService.batchUpdatePhotos(
      store.memorialUuid,
      {
        photos: photosData,
        deletePhotoUuids: photosToDelete.value
      },
      store.apiToken
    )

    if (!response.success) {
      throw new Error(response.msg || 'Failed to save photos')
    }

    for (const audio of audios.value) {
      const updateResponse = await apiService.updateMediaTitle(audio.uuid, audio.title || '', store.apiToken)
      if (!updateResponse.success) throw new Error(updateResponse.msg || 'Failed to update voice memory')
    }

    for (const audioUuid of audiosToDelete.value) {
      const deleteResponse = await apiService.deleteMedia(audioUuid, store.apiToken)
      if (!deleteResponse.success) throw new Error(deleteResponse.msg || 'Failed to remove voice memory')
    }

    for (const audio of newAudios.value) {
      const uploadResponse = await apiService.uploadAudio(
        store.memorialUuid,
        audio.file,
        audio.caption || '',
        store.apiToken
      )
      if (!uploadResponse.success) throw new Error(uploadResponse.msg || `Failed to upload ${audio.name}`)
    }

    if (response.success) {
      console.log('Photos saved successfully')
      router.push('/memory/dashboard')
    } else {
      showError('Failed to save photos: ' + (response.msg || 'Unknown error'))
    }
  } catch (err) {
    console.error('Error saving photos:', err)
    showError('Error saving photos: ' + (err.message || 'Please try again.'))
  } finally {
    saving.value = false
  }
}

const cancel = () => {
  router.push('/memory/dashboard')
}

onMounted(() => {
  loadMemorialData()
})
</script>

<style scoped>
.edit-photos-page {
  min-height: 100vh;
  background: linear-gradient(180deg, var(--v-theme-background) 0%, var(--v-theme-surface) 100%);
}

.main-card {
  border-radius: 12px;
  overflow: hidden;
}

.photo-drop-zone {
  border: 1px dashed var(--v-theme-neutral200);
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: var(--v-theme-background);
}

.photo-drop-zone:hover {
  border-color: var(--v-theme-accent);
  background-color: var(--v-theme-gray100);
}

.photo-drop-zone.drag-over {
  border-color: var(--v-theme-accent);
  background-color: var(--v-theme-gray100);
  transform: scale(1.02);
}

.photo-list {
  max-width: 100%;
}

.photo-row {
  border: 1px solid var(--v-theme-neutral200);
  border-radius: 8px;
  padding: 16px;
  background: white;
}

.photo-container {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
}

.photo-thumbnail {
  border-radius: 8px;
}

.photo-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0;
  transition: opacity 0.2s;
}

.photo-container:hover .photo-overlay {
  opacity: 1;
}

.remove-btn {
  background-color: rgba(255, 255, 255, 0.9) !important;
}

.photo-container.uploading {
  position: relative;
}

.upload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.page-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  /* symmetric gutters reserve room for the profile menu pinned to the right, so
     a long memorial name truncates before it can run underneath the icon */
  padding-left: 64px;
  padding-right: 64px;
}

.brand-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 0;
  max-width: 100%;
}

/* The router-link wrapper has to be shrinkable too, otherwise the name never
   truncates; flex also removes the inline baseline gap under the brand text
   that was pushing the profile icon out of alignment with the title. */
.brand-header > a {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
}

.brand-logo {
  height: 40px;
  width: auto;
}

.brand-title {
  letter-spacing: 0.1rem;
  font-weight: 400;
  color: rgb(var(--v-theme-primary));
  margin: 0;
}

.profile-menu-wrapper {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
}

.section-header {
  font-weight: 600 !important;
  border-bottom: 1px solid var(--v-theme-border);
}
</style>
